import DownloadCSVMenuButton, {
    downloadTypes,
} from 'components/Button/DownloadCSVMenuButton';
import {
    INSTAGRAM_METADATA_BY_KEY,
    INSTAGRAM_SORT_OPTIONS,
    SpateMetricMetadata,
} from 'utils/metrics';
import { MdOutlineShowChart, MdOutlineStackedLineChart } from 'react-icons/md';
import { Message, isDataSupported } from '../utils';
import {
    buildExportFilename,
    downloadItemsAsCSV,
    downloadTimeSeriesAsCSV,
} from 'utils/formatCSV';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCurrentCountry, useEndDate, useInstagramWeeks } from 'utils/hooks';
import AddBySearch from 'components/Navigation/Search/AddBySearch';
import { BannerRow as BannerRowContent } from './BannerRow';
import { DATA_SOURCES } from '@/config/constants';
import DataTable from 'components/DataVis/DataTable/DataTable';
import Drawer from 'components/Drawer';
import ErrorBoundary from 'containers/ErrorBoundary';
import FilterSelect from 'components/Input/FilterSelect';
import IconButton from 'components/Button/IconButton';
import InstagramDrawerContent from 'views/InstagramTrends/InstagramDrawerContent/InstagramDrawerContent';
import InstagramRelatedAggregateDrawerContent from 'components/DataVis/InstagramRelatedCategoriesTable/InstagramRelatedAggregateDrawerContent';
import NameCellWithTooltip from '../NameCellWithTooltip';
import Stack from 'components/Stack';
import ToggleWatch from 'components/Button/ToggleWatch';
import WatchlistInstagramTimeSeries from 'components/DataVis/WatchlistInstagramTimeSeries';
import { calculateInstagramData } from 'utils/helpers';
import { createDefaultColumns } from 'components/DataVis/DataTable/utils';
import downloadImage from 'utils/downloadImage';
import { getOnChange } from 'components/SectionTableFilterChips';
import { getSeriesColors } from 'components/DataVis/Charts/MultiPredictionTimeSeriesChart/utils';
import { getWatchlistRenderHits } from '../Buttons';
import { healthStatFilterOptions } from 'utils/filters';
import isEqual from 'react-fast-compare';
import styled from 'styled-components';
import { useCardsFilterSort } from 'components/CardsWrapper';
import { useInstagramWatchlistData } from './useInstagramWatchlistData';
import useTableColumns from 'utils/hooks/useTableColumns';
import { v4 as uuid } from 'uuid';

const INITIAL_NUM_LINES = 12;

// Regular Instagram metrics (data-store) for brands, categories, hashtags
const instagramMetrics = [
    [INSTAGRAM_METADATA_BY_KEY.non_cum_avg_weekly_likes, true],
    [INSTAGRAM_METADATA_BY_KEY.likes, true],
    [INSTAGRAM_METADATA_BY_KEY.non_cum_increase_WoW, false],
    [INSTAGRAM_METADATA_BY_KEY.non_cum_WoW, true],
    [INSTAGRAM_METADATA_BY_KEY.non_cum_increase_MoM_rolling, false],
    [INSTAGRAM_METADATA_BY_KEY.non_cum_MoM_rolling, false],
    [INSTAGRAM_METADATA_BY_KEY.non_cum_increase_QoQ_rolling, false],
    [INSTAGRAM_METADATA_BY_KEY.non_cum_QoQ_rolling, true],
    [INSTAGRAM_METADATA_BY_KEY.non_cum_increase_YoY_rolling, false],
    [INSTAGRAM_METADATA_BY_KEY.non_cum_YoY_rolling, true],
];

// Likes-based metrics — shown when related-category rows are present.
// Keys must not repeat across the two groups: DataTable keys each cell by
// column key, and a duplicate scrambles row cells on re-render.
// non_cum_avg_weekly_likes is already provided by instagramMetrics.
const likesMetrics = [
    [
        new SpateMetricMetadata({
            ...INSTAGRAM_METADATA_BY_KEY.non_cum_likes_increase_MoM_rolling
                .config,
            tableHeadColumnLabel: `Likes ${INSTAGRAM_METADATA_BY_KEY.non_cum_likes_increase_MoM_rolling.config.tableHeadColumnLabel}`,
        }),
        false,
    ],
    [
        new SpateMetricMetadata({
            ...INSTAGRAM_METADATA_BY_KEY.non_cum_likes_MoM_rolling.config,
            tableHeadColumnLabel: `Likes ${INSTAGRAM_METADATA_BY_KEY.non_cum_likes_MoM_rolling.config.tableHeadColumnLabel}`,
        }),
        false,
    ],
    [
        new SpateMetricMetadata({
            ...INSTAGRAM_METADATA_BY_KEY.non_cum_likes_increase_WoW_rolling
                .config,
            tableHeadColumnLabel: `Likes ${INSTAGRAM_METADATA_BY_KEY.non_cum_likes_increase_WoW_rolling.config.tableHeadColumnLabel}`,
        }),
        false,
    ],
    [
        new SpateMetricMetadata({
            ...INSTAGRAM_METADATA_BY_KEY.non_cum_likes_WoW_rolling.config,
            tableHeadColumnLabel: `Likes ${INSTAGRAM_METADATA_BY_KEY.non_cum_likes_WoW_rolling.config.tableHeadColumnLabel}`,
        }),
        true,
    ],
    [
        new SpateMetricMetadata({
            ...INSTAGRAM_METADATA_BY_KEY.non_cum_likes_increase_QoQ_rolling
                .config,
            tableHeadColumnLabel: `Likes ${INSTAGRAM_METADATA_BY_KEY.non_cum_likes_increase_QoQ_rolling.config.tableHeadColumnLabel}`,
        }),
        false,
    ],
    [
        new SpateMetricMetadata({
            ...INSTAGRAM_METADATA_BY_KEY.non_cum_likes_QoQ_rolling.config,
            tableHeadColumnLabel: `Likes ${INSTAGRAM_METADATA_BY_KEY.non_cum_likes_QoQ_rolling.config.tableHeadColumnLabel}`,
        }),
        true,
    ],
    [
        new SpateMetricMetadata({
            ...INSTAGRAM_METADATA_BY_KEY.non_cum_likes_increase_YoY_rolling
                .config,
            tableHeadColumnLabel: `Likes ${INSTAGRAM_METADATA_BY_KEY.non_cum_likes_increase_YoY_rolling.config.tableHeadColumnLabel}`,
        }),
        false,
    ],
    [
        new SpateMetricMetadata({
            ...INSTAGRAM_METADATA_BY_KEY.non_cum_likes_YoY_rolling.config,
            tableHeadColumnLabel: `Likes ${INSTAGRAM_METADATA_BY_KEY.non_cum_likes_YoY_rolling.config.tableHeadColumnLabel}`,
        }),
        false,
    ],
];

const instagramSortOptions = Object.values(INSTAGRAM_SORT_OPTIONS).filter(
    ({ sortBy }) => instagramMetrics.some(([{ key }]) => key === sortBy),
);

const topicHealthStatKeys = ['average weekly likes', 'posts', 'engagement'];
const instagramHealthStatFilterOptions = healthStatFilterOptions.filter(
    ({ label }) => topicHealthStatKeys.includes(label),
);

const TableTools = styled(Stack).attrs(
    ({ $direction = 'row', $space = '.5em' }) => ({
        $direction,
        $space,
    }),
)`
    display: flex;
    justify-content: flex-end;
`;

const InstagramWatchlist = memo(
    ({
        brandIds,
        categoryIds,
        instagramHashtags,
        relatedCategoryParentCategoryItems = [],
        relatedCategoryParentBrandItems = [],
        categoryPairs = [],
        brandPairs = [],
        countryTextId,
        watchlistId,
        watchlistItems,
        onRemoveItem,
        onAddItem,
        watchlistName,
    }) => {
        // Likes-based metrics are shown when related-category rows are present.
        const hasLikesRows =
            categoryPairs?.length > 0 || brandPairs?.length > 0;

        const activeMetrics = hasLikesRows
            ? [...instagramMetrics, ...likesMetrics]
            : instagramMetrics;
        const country = useCurrentCountry();
        const { week, year } = useInstagramWeeks();
        const date = useEndDate('instagram');
        const defaultInitialSortOption = instagramSortOptions.find(
            ({ sortBy }) => sortBy === instagramMetrics[1][0].key,
        );
        const timeSeriesRef = useRef();
        const [placeholderDataByType, setPlaceholderDataByType] = useState({
            brand: [],
            category: [],
            hashtag: [],
            relatedCategory: [],
        });

        const {
            allInstagramData,
            isDataLoaded,
            isPlaceholderDataLoaded,
            instagramBrandsDataQuery,
            instagramCategoriesDataQuery,
            instagramHashtagsDataQuery,
            allRelatedCategories,
        } = useInstagramWatchlistData({
            brandIds,
            categoryIds,
            instagramHashtags,
            relatedCategoryParentCategoryItems,
            relatedCategoryParentBrandItems,
            categoryPairs,
            brandPairs,
            defaultInitialSortOption,
            placeholderDataByType,
        });
        let handleQueryCacheOnRemoveItem = onRemoveItem;
        if (onRemoveItem && (isDataLoaded || isPlaceholderDataLoaded)) {
            handleQueryCacheOnRemoveItem = (node) => {
                setPlaceholderDataByType({
                    brand: (instagramBrandsDataQuery?.data || []).filter(
                        (d) => d.brand !== node.id,
                    ),
                    category: (instagramCategoriesDataQuery?.data || []).filter(
                        (d) => d.category !== node.id,
                    ),
                    hashtag: (instagramHashtagsDataQuery?.data || []).filter(
                        (d) => d.hashtag !== node.hashtag,
                    ),
                    relatedCategory: (allRelatedCategories || []).filter(
                        (d) => d.id !== node.id,
                    ),
                });
                onRemoveItem(node);
            };
        } else if (onRemoveItem) {
            handleQueryCacheOnRemoveItem = () => {};
        }

        const handleQueryCacheOnAddItem = (node) => {
            const skeletonNode = {
                id: node.id || node.name,
                fetching: true,
                fetchingData: true,
                [node.type]: node.id || node.name,
                type: node.type,
            };
            const brand = JSON.parse(
                JSON.stringify(instagramBrandsDataQuery?.data || []),
            );
            const category = JSON.parse(
                JSON.stringify(instagramCategoriesDataQuery?.data || []),
            );
            const hashtag = JSON.parse(
                JSON.stringify(instagramHashtagsDataQuery?.data || []),
            );
            if (node.type === 'category') {
                category.push(skeletonNode);
            } else if (node.type === 'brand') {
                brand.push(skeletonNode);
            } else if (
                node.type === 'hashtag' ||
                node.type === 'instagram-hashtag'
            ) {
                hashtag.push(skeletonNode);
            }
            setPlaceholderDataByType({ brand, category, hashtag });
            onAddItem(node);
        };

        const renderHits = useMemo(
            () =>
                getWatchlistRenderHits({
                    watchlistItems,
                    onAddItem: onAddItem ? handleQueryCacheOnAddItem : null,
                    onRemoveItem: onRemoveItem
                        ? (elem) => {
                              if (elem.type === 'hashtag') {
                                  // algolia returns type hashtag for all records by default
                                  elem.id =
                                      watchlistItems.find(
                                          ({ name, type }) =>
                                              elem.name === name &&
                                              type === 'instagram-hashtag',
                                      )?.name ?? elem.name;
                              }
                              const node = {
                                  [elem.type]: elem.id,
                                  id: elem.id,
                                  type: elem.type,
                                  name: elem.name,
                                  [elem.brandId ? 'brand' : 'category']:
                                      elem.brandId || elem.categoryId,
                              };
                              handleQueryCacheOnRemoveItem(node);
                          }
                        : null,
                    dataSource: 'instagram',
                }),
            [watchlistItems, onAddItem, onRemoveItem, allInstagramData],
        );

        const unifiedInstagramData = useMemo(() => {
            if (!allInstagramData) return [];
            return allInstagramData
                .map((item) => {
                    let id = null;
                    let name = null;

                    switch (item.type) {
                        case 'instagram-hashtag':
                            id = item.hashtag;
                            name = item.name;
                            break;
                        case 'related-category':
                            id = item.id;
                            name = item.name;
                            break;
                        case 'category':
                            id = item.category;
                            name = item.name;
                            break;
                        case 'brand':
                            id = item.brand;
                            name = item.name;
                            break;
                        default:
                            console.warn(`Unknown item type: ${item.type}`);
                            break;
                    }

                    return {
                        ...item,
                        id,
                        name,
                    };
                })
                .filter((d) =>
                    isDataSupported(d, country, DATA_SOURCES.INSTAGRAM),
                );
        }, [allInstagramData]);

        const {
            filters,
            filtersKey,
            sortOptionKey,
            maxSortMetric,
            onSearchInputChange,
            setFilter,
            setFilters,
            resetFilters,
            initialSortOption,
            sortOption,
            setSortOption: baseSetSortOption,
            sortedData,
        } = useCardsFilterSort({
            rootCategoryId: watchlistId,
            data: unifiedInstagramData,
            sortOptions: instagramSortOptions,
            initialSortOption: defaultInitialSortOption,
            filterMissingData: false,
        });
        const TABLE_NAME = 'watchlist-instagram';
        const { visibleOrderedMetrics, columnsMenu, setSortOption } =
            useTableColumns({
                tableName: TABLE_NAME,
                metrics: activeMetrics,
                setSortOption: baseSetSortOption,
            });

        const setWatchlistSortOption = useCallback(
            (sortOption) => {
                setSortOption(
                    instagramSortOptions.find(
                        ({ sortBy, ascending }) =>
                            sortBy === sortOption.sortBy &&
                            ascending === sortOption.ascending,
                    ),
                );
            },
            [setSortOption],
        );

        const visibleColumnsConfig = createDefaultColumns(
            ['name', ...visibleOrderedMetrics],
            { showHealthStatLabels: true },
        );

        visibleColumnsConfig[0].formatCell = ({ row, value }) => (
            <NameCellWithTooltip row={row} value={value} />
        );

        const [visibleIds, setVisibleIds] = useState(null);
        const [colorsById, setColorsById] = useState(null);
        const [removedColor, setRemovedColor] = useState(null);
        const [viewSumTimeSeries, setViewSumTimeSeries] = useState(false);

        useEffect(() => {
            setVisibleIds(null);
            setColorsById(null);
            setRemovedColor(null);
        }, [filters]);

        const data = [];
        const visibleTimeSeriesData = [];
        const timeSeriesDataIds = [];
        if (sortedData) {
            for (let i = 0; i < sortedData.length; i++) {
                const row = sortedData[i];
                const hasTimeSeriesData =
                    row.timeSeries && row.timeSeries.length;
                const isInitialId =
                    !visibleIds &&
                    visibleTimeSeriesData.length < INITIAL_NUM_LINES &&
                    hasTimeSeriesData;
                const isVisibleId = visibleIds && visibleIds.includes(row.id);
                const checked = !!(isInitialId || isVisibleId);
                const color =
                    hasTimeSeriesData && checked
                        ? (colorsById && colorsById[row.id]) ||
                          getSeriesColors(visibleTimeSeriesData.length)
                        : undefined;
                const formattedRow = { ...row, checked, color };
                data.push(formattedRow);
                if (checked && hasTimeSeriesData)
                    visibleTimeSeriesData.push(formattedRow);
                if (hasTimeSeriesData) timeSeriesDataIds.push(row.id);
            }
        }

        const onToggleCheck = ({ node, checked }) => {
            const nextColorsById = colorsById
                ? { ...colorsById }
                : data.reduce((acc, { id, color }, idx) => {
                      if (color) acc[id] = color;
                      return acc;
                  }, {});

            if (!node) {
                const currentVisible = (
                    !visibleIds
                        ? data.filter(({ checked }) => checked)
                        : visibleIds
                ).length;
                setVisibleIds(
                    !checked && currentVisible < timeSeriesDataIds.length
                        ? timeSeriesDataIds
                        : !checked
                          ? []
                          : null,
                );
                setColorsById(null);
            } else if (checked && visibleIds && !visibleIds.includes(node.id)) {
                setVisibleIds(visibleIds.concat([node.id]));
                nextColorsById[node.id] =
                    removedColor || getSeriesColors(visibleIds.length);
                setColorsById(nextColorsById);
                if (removedColor) setRemovedColor(null);
            } else if (!checked && visibleIds && visibleIds.includes(node.id)) {
                setVisibleIds(visibleIds.filter((id) => id !== node.id));
                setRemovedColor(nextColorsById[node.id]);
                delete nextColorsById[node.id];
                setColorsById(nextColorsById);
            } else if (checked) {
                setVisibleIds(
                    visibleTimeSeriesData.map(({ id }) => id).concat([node.id]),
                );
                nextColorsById[node.id] =
                    removedColor ||
                    getSeriesColors(visibleTimeSeriesData.length);
                setColorsById(nextColorsById);
                if (removedColor) setRemovedColor(null);
            } else {
                setVisibleIds(
                    visibleTimeSeriesData
                        .filter(({ id }) => id !== node.id)
                        .map(({ id }) => id),
                );
                setRemovedColor(nextColorsById[node.id]);
                delete nextColorsById[node.id];
                setColorsById(nextColorsById);
            }
        };

        const unfilteredCount = unifiedInstagramData?.filter(
            ({ time_series }) => time_series && time_series.length,
        ).length;
        const visibleCount = visibleTimeSeriesData
            ? visibleTimeSeriesData.length
            : 0;
        const noDataMessage = !data
            ? ''
            : sortedData.length && !unifiedInstagramData.length
              ? `Not enough data to render time series chart.`
              : `No trends available.${Object.keys(filters).length ? ' Try adjusting the filters.' : ''}`;
        const mixpanelMeta = {
            contentKey: 'InstagramWatchlist',
            country: country ? country.name : null,
        };

        const renderDrawerContent = (selectedId) => {
            const dataPoint =
                selectedId &&
                sortedData &&
                sortedData.find(({ id }) => id === selectedId);
            if (!dataPoint) return <Message>Item not found.</Message>;
            if (dataPoint.type === 'related-category') {
                const isRelatedBrand = dataPoint.relatedType === 'brand';
                return (
                    <ErrorBoundary>
                        <InstagramRelatedAggregateDrawerContent
                            entity={isRelatedBrand ? 'brand' : 'category'}
                            selectedRow={dataPoint}
                            week={week}
                            year={year}
                            countries={country?.id ? [country.id] : null}
                            categories={
                                dataPoint.categoryId
                                    ? [dataPoint.categoryId]
                                    : null
                            }
                            brands={
                                dataPoint.brandId ? [dataPoint.brandId] : null
                            }
                            relatedCategories={
                                !isRelatedBrand && dataPoint.relatedCategoryId
                                    ? [dataPoint.relatedCategoryId]
                                    : null
                            }
                            relatedBrands={
                                isRelatedBrand && dataPoint.relatedCategoryId
                                    ? [dataPoint.relatedCategoryId]
                                    : null
                            }
                            name={dataPoint.name}
                            parentName={dataPoint.relatedName}
                            showAddToWatchlist={false}
                        />
                    </ErrorBoundary>
                );
            }
            return (
                <ErrorBoundary>
                    <InstagramDrawerContent
                        dataPoint={{
                            ...dataPoint,
                            type:
                                dataPoint.type === 'instagram-hashtag'
                                    ? 'hashtag'
                                    : dataPoint.type,
                        }} // InstagramDrawerContent expects type hashtag for instagram hashtags
                        showAddToWatchlist={false}
                    />
                </ErrorBoundary>
            );
        };

        const rowHeight = sortedData?.find(
            (s) =>
                (s.type === 'instagram-hashtag' && s.brand) ||
                s.type === 'related-category',
        )
            ? 75
            : 50;

        return (
            <Stack $space="1.5rem">
                <div
                    css={`
                        padding: 1.25rem;
                        padding-top: 0;
                        margin: 0 -1.25rem 1.25rem;
                        border-bottom: 1px solid ${({ theme }) => theme.gray_5};
                        display: flex;
                        justify-content: space-between;
                    `}
                >
                    <IconButton
                        size="sm"
                        IconLeft={
                            viewSumTimeSeries
                                ? MdOutlineStackedLineChart
                                : MdOutlineShowChart
                        }
                        onClick={(e) =>
                            setViewSumTimeSeries(!viewSumTimeSeries)
                        }
                        mixpanelContext={
                            viewSumTimeSeries
                                ? 'View Separated Time Series'
                                : 'View Combined Time Series'
                        }
                        isDisabled={!visibleCount}
                    >
                        {viewSumTimeSeries ? 'View Separated' : 'View Combined'}
                    </IconButton>
                    <DownloadCSVMenuButton
                        isDisabled={!isDataLoaded || !date}
                        isLoading={!isDataLoaded || !date}
                        options={[
                            {
                                mixpanelMeta: { label: 'card data csv' },
                                children: 'Table Data CSV',
                                onClick: () =>
                                    downloadItemsAsCSV(
                                        buildExportFilename({
                                            entity: watchlistName,
                                            country,
                                            source: 'instagram',
                                            type: 'trend',
                                            kind: 'data',
                                            date,
                                        }),
                                        sortedData,
                                        [
                                            {
                                                key: 'name',
                                                accessor: ({
                                                    exportName,
                                                    name,
                                                }) => exportName || name || '',
                                            },
                                            ...visibleOrderedMetrics.map(
                                                ({
                                                    key,
                                                    tableHeadColumnLabel: label,
                                                }) => ({ key, label }),
                                            ),
                                            {
                                                key: 'cluster',
                                                label: 'Cluster',
                                                accessor: ({ cluster }) =>
                                                    cluster
                                                        ? cluster.replace(
                                                              'instagram ',
                                                              '',
                                                          )
                                                        : cluster,
                                            },
                                            { key: 'healthStats' },
                                        ],
                                    ),
                                downloadType: downloadTypes.CSV,
                                key: 'card data csv',
                            },
                            {
                                mixpanelMeta: { label: 'time series csv' },
                                children: 'Time Series CSV',
                                onClick: () =>
                                    downloadTimeSeriesAsCSV(
                                        buildExportFilename({
                                            entity: watchlistName,
                                            country,
                                            source: 'instagram',
                                            type: 'trend',
                                            kind: 'time-series',
                                            date,
                                        }),
                                        sortedData,
                                        {
                                            tsColumns: [
                                                { key: 'date' },
                                                {
                                                    key: 'real_volume',
                                                    label: 'volume',
                                                },
                                                {
                                                    key: 'backfill',
                                                    label: 'estimated',
                                                },
                                            ],
                                        },
                                    ),
                                downloadType: downloadTypes.CSV,
                                key: 'time series csv',
                            },
                            {
                                mixpanelMeta: {
                                    label: 'time series image',
                                },
                                children: 'Time Series Image',
                                onClick: () =>
                                    downloadImage(timeSeriesRef.current, {
                                        title: `${watchlistName} Instagram (${country ? country.code || country.name : null})`,
                                        filename: buildExportFilename({
                                            date,
                                            country,
                                            source: 'instagram',
                                            entity: watchlistName,
                                            type: 'trend',
                                            kind: 'time-series',
                                        }),
                                    }),
                                downloadType: downloadTypes.IMAGE,
                                key: 'time series image',
                            },
                        ]}
                        mixpanelMeta={mixpanelMeta}
                    />
                </div>
                <WatchlistInstagramTimeSeries
                    data={visibleTimeSeriesData}
                    metrics={
                        visibleTimeSeriesData.length
                            ? calculateInstagramData(visibleTimeSeriesData)
                            : {}
                    }
                    isLoading={!isDataLoaded}
                    ref={timeSeriesRef}
                    chartHeight={350}
                    showStatDisplay
                    showDataSourceBar={false}
                    customCss="display: block;"
                    viewSumTimeSeries={viewSumTimeSeries}
                    week={week}
                    year={year}
                />
                <div
                    css={`
                        padding: ${({ theme }) => theme.space_8}px 0;
                        font-weight: bold;
                        font-size: 0.75em;
                        margin-bottom: 12px;
                    `}
                >
                    {`Showing ${!isDataLoaded ? '... of ...' : `${visibleCount} of ${unfilteredCount}`} items. Use the list to add and remove items.`}
                </div>
                <Stack $space={(theme) => `${theme.space_10}px;`}>
                    <TableTools
                        css={`
                            display: flex;
                            justify-content: space-between;
                        `}
                    >
                        <FilterSelect
                            filters={filters}
                            setFilter={setFilter}
                            setFilters={setFilters}
                            resetFilters={resetFilters}
                            sortOption={sortOption}
                            setSortOption={setWatchlistSortOption}
                            initialSortOption={initialSortOption}
                            mixpanelMeta={mixpanelMeta}
                            menus={['instagramCluster', 'healthStats']}
                            healthStatFilterOptions={
                                instagramHealthStatFilterOptions
                            }
                            getOnChange={getOnChange}
                            showPresetsMenu={false}
                            showAllHealthStats={true}
                            menuAlign="left"
                        />
                        <div
                            css={`
                                display: flex;
                                gap: 12px;
                            `}
                        >
                            {columnsMenu}
                            {renderHits ? (
                                <AddBySearch
                                    countryTextId={countryTextId}
                                    renderHits={renderHits}
                                    panelAlign="right"
                                    dataSource="instagram"
                                />
                            ) : null}
                        </div>
                    </TableTools>
                    <DataTable
                        dataKey={`d-*-s-${sortOptionKey}-f-${filtersKey}`}
                        week={week}
                        year={year}
                        maxSortMetric={maxSortMetric}
                        onSearchInputChange={onSearchInputChange}
                        sortOption={sortOption}
                        setSortOption={setWatchlistSortOption}
                        initialSortOption={initialSortOption}
                        mixpanelMeta={mixpanelMeta}
                        noDataMessage={noDataMessage}
                        rowHeight={rowHeight}
                        data={isDataLoaded || data?.length ? data : undefined}
                        name={watchlistName || TABLE_NAME}
                        onRemoveItem={handleQueryCacheOnRemoveItem}
                        onToggleCheck={onToggleCheck}
                        columns={visibleColumnsConfig}
                        renderDrawerContent={renderDrawerContent}
                        DrawerComponent={Drawer}
                        BannerRowContent={
                            <BannerRowContent
                                watchlistId={watchlistId}
                                mixpanelMeta={mixpanelMeta}
                            />
                        }
                    />
                </Stack>
            </Stack>
        );
    },
    isEqual,
);

export default InstagramWatchlist;
